import { jsx as _jsx } from "react/jsx-runtime";
// src/plugins/builder/templates/dispute-won-slack/generator.tsx
// Fires a Slack webhook whenever a dispute moves to "won". Hook+UI so the kid
// sees the list of recent wins on the plugin page.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
import { SimplePage } from '../../runtime/SimplePage';
const RuntimeUi = (ctx) => {
    const cfg = ctx.config;
    return (_jsx(SimplePage, { title: ctx.instanceName, subtitle: `When a dispute is won, send ${cfg.celebrate ? '🎉 ' : ''}Slack message.`, source: "disputes", filterDispute: (d) => d.status === 'won', emptyTitle: "No wins yet", emptyBody: "When a dispute is marked as won, you'll see it here and Slack will get pinged." }));
};
const runtimeHooks = {
    async onDisputeResolved(ctx, dispute) {
        const d = dispute;
        if (d.status !== 'won')
            return;
        const cfg = ctx.config;
        if (!cfg.slackWebhook)
            return;
        if (!ctx.permissions.includes('external:fetch'))
            return;
        const emoji = cfg.celebrate ? ':tada: :trophy: ' : '';
        await fetch(cfg.slackWebhook, {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ text: `${emoji}We just won a dispute! "${d.claim_summary.slice(0, 140)}"` }),
        }).catch(() => { });
    },
};
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId, name: params.instanceName,
        description: `Pings Slack ${cfg.celebrate ? 'with emojis ' : ''}when a dispute is won.`,
        category: 'automation', icon: 'Trophy',
        permissions: ['read:disputes', 'external:fetch', 'ui:toast'],
        entry: 'both', hooks: { onDisputeResolved: true },
        config: meta.configSchema,
        defaultConfig: { slackWebhook: cfg.slackWebhook, celebrate: cfg.celebrate },
    });
    const pluginTsx = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import { useDisputes } from '../sdk';
import { EmptyState } from '../../ui/EmptyState';

export default function ${cls(params.instanceId)}() {
  const { disputes } = useDisputes();
  const won = (disputes ?? []).filter(d => d.status === 'won');
  if (won.length === 0) return <main id="main" className="mx-auto max-w-3xl px-6 py-10"><h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1><EmptyState title="No wins yet" body="Won disputes will show up here." /></main>;
  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1>
      <ul className="mt-6 space-y-3">
        {won.map(d => (<li key={d.id} className="rounded-2xl bg-white p-4 ring-1 ring-slate-200"><p className="text-sm font-medium">Won · {d.claim_summary}</p></li>))}
      </ul>
    </main>
  );
}
`;
    const hooksTs = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import type { PluginHandlers, HandlerContext } from '../types';
export const handlers: PluginHandlers = {
  async onDisputeResolved(ctx: HandlerContext, dispute: unknown) {
    const d = dispute as { status: string; claim_summary: string };
    if (d.status !== 'won') return;
    if (!ctx.permissions.includes('external:fetch')) return;
    const emoji = ${cfg.celebrate ? "':tada: :trophy: '" : "''"};
    await fetch('${esc(cfg.slackWebhook)}', {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ text: \`\${emoji}We just won a dispute! "\${d.claim_summary.slice(0, 140)}"\` }),
    }).catch(() => {});
  },
};
export default handlers;
`;
    return assemble({
        manifest, pluginTsx, hooksTs,
        readmeMd: readme({ name: params.instanceName, templateId: meta.id,
            description: `Sends a Slack message every time a dispute is marked "won".`,
            configSummary: [['slackWebhook', cfg.slackWebhook], ['celebrate', String(cfg.celebrate)]],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
function cls(id) { return 'P_' + id.replace(/[^a-zA-Z0-9]+/g, '_'); }
const template = {
    id: meta.id, name: meta.name, tagline: meta.tagline, icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    hooks: meta.hooks,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: () => 'Slack wins notifier',
    describe: (cfg) => `Post to Slack${cfg.celebrate ? ' with 🎉 emojis' : ''} whenever we win a dispute.`,
    generate, runtimeUi: RuntimeUi, runtimeHooks,
    clickCount: meta.clickCount,
};
export default template;
